import * as React from 'react'
import { inject, observer } from 'mobx-react'
import Tooltip from 'react-tooltip'
import moment from 'moment'
import { Modal, ModalHeader, ModalBody } from 'reactstrap'
import { ModifyReasonCard } from './ModifyReasonCard'
import { InventoryLogType, IInventoryLog } from '@getgreenline/inventory/build/models/inventoryLog'
import { CurrentCompanyStore } from '../../../../../stores/CurrentCompanyStore'
import { FormError } from '../../../../../components/FormError'
import { parseErrorMsg } from '../../../../../utilities/helpers'
import { Track } from '../../../../../Track'
import { BLAZE_COMPANY } from '../../../../../constants/general'
import { InventoryLogAPI } from '@getgreenline/inventory'

interface IProps {
  onHide: () => void
  onUpdate: () => void
  log: IInventoryLog
}

interface InjectedProps {
  currentCompanyStore?: CurrentCompanyStore
}

interface IState {
  isApplying: boolean
  newLogType: InventoryLogType | undefined
  errorMessage: string
}

@inject('currentCompanyStore')
@observer
export class ModifyLogModal extends React.Component<IProps & InjectedProps, IState> {
  constructor(props: IProps & InjectedProps) {
    super(props)
    this.state = {
      newLogType: undefined,
      isApplying: false,
      errorMessage: '',
    }
  }

  apply = async () => {
    const { newLogType } = this.state
    const { currentCompanyStore, log } = this.props
    const companyId = currentCompanyStore?.company?.id
    if (!companyId) {
      return this.setState({ errorMessage: 'No company Id found' })
    }
    if (!newLogType) {
      return this.setState({ errorMessage: 'New log type needs to be selected to apply' })
    }

    this.setState({ isApplying: true })
    try {
      await InventoryLogAPI.updateInventoryLog(companyId, log.id, newLogType)

      this.props.onUpdate()

      Track.track('dashboard:inventory_inventoryLogTypeModified', {
        inventoryLogId: log.id,
        oldType: log.type,
        newType: newLogType,
      })
    } catch (error) {
      this.setState({
        isApplying: false,
        errorMessage: parseErrorMsg(error) || 'Could not modify the inventory log type',
      })
    }
  }

  render() {
    const { newLogType, isApplying, errorMessage } = this.state
    const { onHide, log, currentCompanyStore } = this.props

    const updateValue = log.updateValue
    return (
      <Modal isOpen={true} toggle={onHide} autoFocus={false} size='md'>
        <ModalHeader toggle={onHide}>Modify inventory log</ModalHeader>
        <ModalBody>
          <div>
            On {moment(log.createDate).format('MMM DD, h:mma')},
            <br />
            <b>{log.userName}</b> recorded an audit with a discrepancy of {updateValue > 0 && '+'}
            <b className={(updateValue >= 0 ? 'text-success' : 'text-danger') + ' mr-1'}>
              {updateValue}
            </b>
            for {log.productName}. Please select a valid Health Canada reason below.
          </div>

          <br />

          <div className='text-muted'>
            Why do I have to modify my inventory log reasons?{' '}
            <span
              data-tip
              data-for='why'
              className='fa fa-info-circle ml-2'
              style={{ cursor: 'pointer' }}
            />
          </div>
          <Tooltip place='bottom' id='why' effect='solid'>
            In {BLAZE_COMPANY}, audited products will show up as "other additions" or "other
            reductions" on your compliance reports.
            <br />
            Health Canada would like unaccounted for discrepancies to be reduced as much as
            possible.
            <br />
            <br />
            If your additions and reductions for a product are the same (ex. +50 and -50), then they
            can be safely ignored.
            <br />
            {BLAZE_COMPANY} will automatically cancel them out.
            <br />
            <br />
            Small discrepancies in the single digits are understandable in a retail setting. Double
            digits are not.
          </Tooltip>

          <br />

          {updateValue > 0 ? (
            <>
              {/* Inventory addition options */}
              <div className='row'>
                <div className='col-md-6 mb-4'>
                  <ModifyReasonCard
                    title='Purchase order'
                    description='Select if you added inventory but forgot to add it as purchase order. Common mistake for first-time store owners.'
                    checked={newLogType === InventoryLogType.PURCHASE_ORDER}
                    onClick={() => this.setState({ newLogType: InventoryLogType.PURCHASE_ORDER })}
                  />
                </div>
                <div className='col-md-6 mb-4'>
                  <ModifyReasonCard
                    title='Audit'
                    description='Select if you do not know the reason.'
                    checked={newLogType === InventoryLogType.AUDIT}
                    onClick={() => this.setState({ newLogType: InventoryLogType.AUDIT })}
                  />
                </div>
                {currentCompanyStore!.company!.province !== 'ON' && (
                  <div className='col-md-6 mb-4'>
                    <ModifyReasonCard
                      title='Refunded'
                      description='Select if you refunded this product.'
                      checked={newLogType === InventoryLogType.REFUND}
                      onClick={() => this.setState({ newLogType: InventoryLogType.REFUND })}
                    />
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              {/* Inventory reduction options */}
              <div className='row'>
                <div className='col-md-6 mb-4'>
                  <ModifyReasonCard
                    title='Returned'
                    description='Select if you returned this product to your supplier.'
                    checked={newLogType === InventoryLogType.RETURN}
                    onClick={() => this.setState({ newLogType: InventoryLogType.RETURN })}
                  />
                </div>
                <div className='col-md-6 mb-4'>
                  <ModifyReasonCard
                    title='Destroyed'
                    description='Select if destroyed this product.'
                    checked={newLogType === InventoryLogType.DESTROYED}
                    onClick={() => this.setState({ newLogType: InventoryLogType.DESTROYED })}
                  />
                </div>
                <div className='col-md-6 mb-4'>
                  <ModifyReasonCard
                    title='Lost'
                    description='Select if you lost this product.'
                    checked={newLogType === InventoryLogType.LOST}
                    onClick={() => this.setState({ newLogType: InventoryLogType.LOST })}
                  />
                </div>
                <div className='col-md-6 mb-4'>
                  <ModifyReasonCard
                    title='Audit'
                    description='Select if you do not know the reason.'
                    checked={newLogType === InventoryLogType.AUDIT}
                    onClick={() => this.setState({ newLogType: InventoryLogType.AUDIT })}
                  />
                </div>
                {currentCompanyStore!.company!.province !== 'ON' && (
                  <div className='col-md-6 mb-4'>
                    <ModifyReasonCard
                      title='Sale'
                      description={`Select if you sold this product but did not record it on ${BLAZE_COMPANY}.`}
                      checked={newLogType === InventoryLogType.SALE}
                      onClick={() => this.setState({ newLogType: InventoryLogType.SALE })}
                    />
                  </div>
                )}
              </div>
            </>
          )}

          <div>
            <div className='mt-3'>
              <button
                className='w-100 btn btn-success btn-lg'
                onClick={this.apply}
                disabled={!newLogType || isApplying}
              >
                Modify
              </button>
            </div>

            {!!errorMessage && (
              <div className='mt-3'>
                <FormError message={errorMessage} />
              </div>
            )}
          </div>
        </ModalBody>
      </Modal>
    )
  }
}
